import { Trash2 } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Form } from "@/shared/components/ui/form";

// Shared chrome for the create/edit form dialogs. Generic over input/output types so onSubmit is typed to the transformed values.
export function FormDialog<
  TFieldValues extends FieldValues,
  TTransformed extends FieldValues,
>({
  open,
  onOpenChange,
  title,
  description,
  form,
  onSubmit,
  submitting,
  submitLabel = "Spara",
  submitIcon,
  onDelete,
  deleting,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: ReactNode;
  form: UseFormReturn<TFieldValues, unknown, TTransformed>;
  onSubmit: (values: TTransformed) => void;
  submitting: boolean;
  submitLabel?: string;
  submitIcon?: ReactNode;
  // Omit to hide the delete button (create mode).
  onDelete?: () => void;
  deleting?: boolean;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {children}
            <DialogFooter>
              {onDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="sm:mr-auto"
                  loading={deleting}
                  onClick={onDelete}
                >
                  <Trash2 />
                  Ta bort
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                disabled={submitting}
                onClick={() => onOpenChange(false)}
              >
                Avbryt
              </Button>
              <Button type="submit" loading={submitting}>
                {submitIcon}
                {submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Shared mutate() callbacks: close on success, show the error dialog on failure.
export function useCloseOrError(onOpenChange: (open: boolean) => void) {
  const { showApiError } = useErrorDialog();
  return (title: string) => ({
    onSuccess: () => onOpenChange(false),
    onError: (e: unknown) => showApiError(e, { title }),
  });
}

// Enter submits; Shift+Enter newlines; skips during IME composition.
export function submitOnEnter(submit: () => void) {
  return (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };
}
